import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import {
  useAuth,
  useBraveUpSurveysApi,
  useQuestionProApi,
  useCalcPercentageData,
  useSendMetricsQuestionPro,
} from '@BUP/hooks';
import { BupSpinner, ErrorMessage } from '@BUP/components';
import './styles.css';
import { useTranslation } from 'react-i18next';

const AdvanceTable = () => {
  const { user } = useAuth();
  const [t] = useTranslation();
  const organizationId = user?.organizations?.id;
  const roleId = user?.roles?.id;
  const enrollmentId = user?.enrollmentId;
  const rolName = user?.roles?.name;
  const { isLoadingSurvey, survey, errorSurvey } = useBraveUpSurveysApi();
  const {
    isLoadingFolders,
    folder,
    errorFolders,
    requestQuestionPro,
  } = useQuestionProApi(survey, rolName);
  const { sendMetricsQP } = useSendMetricsQuestionPro();
  const requestUrl = requestQuestionPro?.request?.responseURL;
  const {
    percentage,
    collectedSurveys,
    calculatePercentage,
  } = useCalcPercentageData(survey, folder);
  const initialStateSum = {
    totalMembers: null,
    totalMinSurveys: null,
    totalCollectedSurveys: null,
    totalPercentage: null,
  };
  const [sumValues, setSumValues] = useState(initialStateSum);
  const calculateSum = (val1, val2, val3, val4) => {
    return val1 || val2 || val3 || val4 ? val1 + val2 + val3 + val4 : 0;
  };

  useEffect(() => {
    setSumValues({
      totalMembers: calculateSum(
        survey?.parents,
        survey?.students,
        survey?.teachers,
        survey?.teaching_assistants
      ),
      totalMinSurveys: calculateSum(
        survey?.parentsMin,
        survey?.studentsMin,
        survey?.teachersMin,
        survey?.teachingAssistansMin
      ),
      totalCollectedSurveys: calculateSum(
        collectedSurveys?.parents,
        collectedSurveys?.students,
        collectedSurveys?.teachers,
        collectedSurveys?.assistants
      ),
      totalPercentage: calculatePercentage(
        calculateSum(
          collectedSurveys?.parents,
          collectedSurveys?.students,
          collectedSurveys?.teachers,
          collectedSurveys?.assistants
        ),
        calculateSum(
          survey?.parentsMin,
          survey?.studentsMin,
          survey?.teachersMin,
          survey?.teachingAssistansMin
        )
      ),
    });
  }, [survey, percentage]);

  const setTextRecomendation = (value) => {
    let text = '';
    if (value <= 70 && value !== null) {
      text = t('pages.data.advanceStatus.message1');
    } else if (value >= 71 && value <= 99 && value !== null) {
      text = t('pages.data.advanceStatus.message2');
    } else if (value > 99 && value !== null) {
      text = t('pages.data.advanceStatus.message3');
    } else {
      text = null;
    }
    return text;
  };

  useEffect(() => {
    sendMetricsQP({
      data: {
        requestUrl,
        isConsumeFromQP: true,
        isConsumeFromBraveup: false,
        organizationId,
        roleId,
        enrollmentId,
        // timeInSeconds: 1,
      },
    });
  }, [requestUrl]);

  const fontColor = (value) => {
    let color = '';
    if (value <= 70 && value !== null) {
      color = 'red';
    } else if (value >= 71 && value <= 99 && value !== null) {
      color = 'var(--global-color-orange-bup)';
    } else if (value > 99 && value !== null) {
      color = 'green';
    } else {
      color = null;
    }
    return color;
  };
  return (
    <>
      {isLoadingSurvey || isLoadingFolders ? (
        <BupSpinner size="large" />
      ) : errorSurvey || errorFolders ? (
        <ErrorMessage />
      ) : (
        <>
          <Row gutter={[16, 16]} className="advaceTable_row-header">
            <Col
              span={5}
              className="advanceTable_grid_col-header advanceTable_grid-first-item"
            />
            <Col span={3} className="advanceTable_grid_col-header">
              {t('pages.data.advanceStatus.tableHeadText1')}
            </Col>
            <Col span={4} className="advanceTable_grid_col-header">
              {t('pages.data.advanceStatus.tableHeadText2')}
            </Col>
            <Col span={4} className="advanceTable_grid_col-header">
              {t('pages.data.advanceStatus.tableHeadText3')}
            </Col>
            <Col span={3} className="advanceTable_grid_col-header">
              {t('pages.data.advanceStatus.tableHeadText4')}
            </Col>
            <Col
              span={5}
              className="advanceTable_grid_col-header advanceTable_grid-last-item"
            >
              {t('pages.data.advanceStatus.tableHeadText5')}
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="advanceTable_grid_row-body">
            <Col span={5} className="advanceTable_grid_col-body">
              {t('pages.data.advanceStatus.tableRowText1')}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              {survey?.teachers || survey?.teachers === 0
                ? survey?.teachers
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {survey?.teachersMin || survey?.teachersMin === 0
                ? survey?.teachersMin
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {collectedSurveys?.teachers || collectedSurveys?.teachers === 0
                ? collectedSurveys?.teachers
                : '-'}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              <b style={{ color: fontColor(percentage?.teachers) }}>
                {percentage?.teachers || percentage?.teachers === 0
                  ? `${percentage?.teachers}%`
                  : '-'}
              </b>
            </Col>
            <Col span={5} className="advanceTable_grid_col-body">
              {setTextRecomendation(percentage?.teachers)}
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="advanceTable_grid_row-body">
            <Col span={5} className="advanceTable_grid_col-body">
              {t('pages.data.advanceStatus.tableRowText2')}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              {survey?.teaching_assistants || survey?.teaching_assistants === 0
                ? survey?.teaching_assistants
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {survey?.teachingAssistansMin ||
              survey?.teachingAssistansMin === 0
                ? survey?.teachingAssistansMin
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {collectedSurveys?.assistants ||
              collectedSurveys?.assistants === 0
                ? collectedSurveys?.assistants
                : '-'}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              <b style={{ color: fontColor(percentage?.assistants) }}>
                {percentage?.assistants || percentage?.assistants === 0
                  ? `${percentage?.assistants}%`
                  : '-'}
              </b>
            </Col>
            <Col span={5} className="advanceTable_grid_col-body">
              {setTextRecomendation(percentage?.assistants)}
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="advanceTable_grid_row-body">
            <Col span={5} className="advanceTable_grid_col-body">
              {t('pages.data.advanceStatus.tableRowText3')}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              {survey?.parents || survey?.parents === 0 ? survey?.parents : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {survey?.parentsMin || survey?.parentsMin === 0
                ? survey?.parentsMin
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {collectedSurveys?.parents || collectedSurveys?.parents === 0
                ? collectedSurveys?.parents
                : '-'}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              <b style={{ color: fontColor(percentage?.parents) }}>
                {percentage?.parents || percentage?.parents === 0
                  ? `${percentage?.parents}%`
                  : '-'}
              </b>
            </Col>
            <Col span={5} className="advanceTable_grid_col-body">
              {setTextRecomendation(percentage?.parents)}
            </Col>
          </Row>
          <Row gutter={[16, 16]} className="advanceTable_grid_row-body">
            <Col span={5} className="advanceTable_grid_col-body">
              {t('pages.data.advanceStatus.tableRowText4')}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              {survey?.students || survey?.students === 0
                ? survey?.students
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {survey?.studentsMin || survey?.studentsMin === 0
                ? survey?.studentsMin
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {collectedSurveys?.students || collectedSurveys?.students === 0
                ? collectedSurveys?.students
                : '-'}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              <b style={{ color: fontColor(percentage?.students) }}>
                {percentage?.students || percentage?.students === 0
                  ? `${percentage?.students}%`
                  : '-'}
              </b>
            </Col>
            <Col span={5} className="advanceTable_grid_col-body">
              {setTextRecomendation(percentage?.students)}
            </Col>
          </Row>
          <Row
            gutter={[16, 16]}
            className="advanceTable_grid_row-body advanceTable_grid_last-row"
          >
            <Col span={5} className="advanceTable_grid_col-body">
              {t('pages.data.advanceStatus.tableRowText5')}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              {sumValues?.totalMembers || sumValues?.totalMembers === 0
                ? sumValues?.totalMembers
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {sumValues?.totalMinSurveys || sumValues?.totalMinSurveys === 0
                ? sumValues?.totalMinSurveys
                : '-'}
            </Col>
            <Col span={4} className="advanceTable_grid_col-body">
              {sumValues?.totalCollectedSurveys ||
              sumValues?.totalCollectedSurveys === 0
                ? sumValues?.totalCollectedSurveys
                : '-'}
            </Col>
            <Col span={3} className="advanceTable_grid_col-body">
              <b style={{ color: 'var(--global-color-grey-simple)' }}>
                {sumValues?.totalPercentage}
                {sumValues?.totalPercentage ? '%' : null}
              </b>
            </Col>
            <Col span={5} className="advanceTable_grid_col-body" />
          </Row>
        </>
      )}
    </>
  );
};

export default AdvanceTable;
